import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../context/PermissionsContext';
import { Avatar, Icon, LogoutIcon } from './core';
import { adjustBrightness } from '../utils/color';

export interface NavbarProps {
  /** User's display name — used to derive initials */
  displayName: string | null;
  /** Called when the user clicks Sign out */
  onSignOut: () => void;
  /** Called when the user clicks the AgroBook logo — navigates to org chooser */
  onLogoClick?: () => void;
  /** Called when the user clicks the org name pill — navigates to org dashboard */
  onOrgNameClick?: () => void;
  /** Called when the user clicks "Edit Profile" in the avatar menu */
  onAvatarClick?: () => void;
  /** Current org name — shown next to the logo when inside an org context */
  orgName?: string;
}

/** Derives up to 2 initials from a display name. */
function getInitials(displayName: string | null): string {
  if (!displayName?.trim()) return '?';
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Navbar({ displayName, onSignOut, onLogoClick, onOrgNameClick, onAvatarClick, orgName }: NavbarProps) {
  const { t } = useTranslation();
  const { appUser } = usePermissions();
  const initials = getInitials(displayName);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const customGradient = appUser?.customAvatarColor
    ? `linear-gradient(135deg, ${adjustBrightness(appUser.customAvatarColor, 1.3)} 0%, ${appUser.customAvatarColor} 100%)`
    : undefined;

  // Close on outside click or Escape key
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  function handleEditProfile() {
    setMenuOpen(false);
    onAvatarClick?.();
  }

  function handleSignOut() {
    setMenuOpen(false);
    onSignOut();
  }

  return (
    <nav className="sticky top-0 z-20 flex h-[60px] items-center justify-between border-b border-green-200 bg-white px-6 sm:h-[54px] sm:px-4">
      {/* Brand */}
      <div className="flex items-center gap-2">
        {/* Logo — always navigates to org chooser */}
        <button
          onClick={onLogoClick}
          className="transition-agrobook flex items-center gap-2 font-display text-xl font-semibold tracking-tight text-green-900 duration-agrobook hover:text-green-700 sm:text-lg"
        >
          <span aria-hidden="true">🌱</span>
          {/* Desktop: show AgroBook wordmark; version only when no org context */}
          <div className={`flex flex-col items-start${orgName ? ' hidden sm:flex' : ''}`}>
            <span className="font-logo">{t('app.name')}</span>
            {!orgName && (
              <span className="font-body text-[10px] font-normal tracking-normal text-gray-400">
                v{__APP_VERSION__}
              </span>
            )}
          </div>
        </button>

        {/* Org name — navigates to org dashboard */}
        {orgName && (
          <>
            <span className="hidden text-green-400 sm:inline" aria-hidden="true">/</span>
            <button
              onClick={onOrgNameClick}
              className="transition-agrobook duration-agrobook hover:opacity-80"
            >
              {/* Desktop pill */}
              <span className="hidden rounded-full bg-green-100 px-3 py-0.5 font-body text-[0.82rem] font-semibold text-green-700 sm:inline">
                {orgName}
              </span>
              {/* Mobile pill */}
              <span className="rounded-full bg-green-100 px-3 py-0.5 font-body text-[0.9rem] font-semibold text-green-700 sm:hidden">
                {orgName}
              </span>
            </button>
          </>
        )}
      </div>

      {/* Right side — avatar menu only */}
      <div ref={menuRef} className="relative">
        <button
          onClick={() => setMenuOpen(o => !o)}
          className="transition-agrobook rounded-full outline-none duration-agrobook hover:ring-2 hover:ring-green-600 hover:ring-offset-1"
          aria-label={t('nav.profile')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Avatar
            size="sm"
            variant="user"
            avatarColor={appUser?.avatarColor}
            customGradient={customGradient}
          >
            {initials}
          </Avatar>
        </button>

        {menuOpen && (
          <div className="absolute end-0 top-[calc(100%+8px)] z-50 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
            {/* Header */}
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="truncate font-body text-sm font-semibold text-gray-900">
                {displayName ?? '—'}
              </p>
              <p className="truncate font-body text-xs text-gray-500">
                {appUser?.email ?? ''}
              </p>
            </div>

            {/* Edit Profile */}
            <div className="border-b border-gray-100 py-1">
              <button
                onClick={handleEditProfile}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 font-body text-sm text-gray-700 transition-colors hover:bg-green-50 hover:text-green-900"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="8" r="4"/>
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
                {t('nav.editProfile')}
              </button>
            </div>

            {/* Sign out */}
            <div className="border-b border-gray-100 py-1">
              <button
                onClick={handleSignOut}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 font-body text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
              >
                <Icon size="sm">
                  <LogoutIcon />
                </Icon>
                {t('nav.signOut')}
              </button>
            </div>

            {/* Version footer */}
            <div className="px-4 py-2.5">
              <p className="font-body text-[11px] text-gray-400">
                {t('app.name')} v{__APP_VERSION__}
              </p>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
